import React, { useState } from "react";
import { api } from "@/lib/http";
import { fmtCurrency } from "@/lib/calc";
import { fmtDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Check, Circle, Gift, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const Dots = ({ total, remaining, testid }) => (
  <span className="inline-flex flex-wrap gap-1 align-middle" data-testid={testid} aria-label={`${remaining} of ${total} remaining`}>
    {Array.from({ length: Math.min(total, 24) }).map((_, i) => <span key={i} className={`h-2.5 w-2.5 rounded-full ${i < total - remaining ? "bg-muted-foreground/30" : "bg-primary"}`} />)}
  </span>
);

// Client-file PACKAGES: active first, progress dots, bribes with ISSUE PRODUCT, original agreed terms.
export const ClientPackages = ({ packages, cur, isAdmin, onChanged }) => {
  const [termsFor, setTermsFor] = useState(null);
  const list = [...(packages || [])].sort((a, b) => ((a.status === "active" || !a.status) ? 0 : 1) - ((b.status === "active" || !b.status) ? 0 : 1) || (b.purchased_at || b.created_at || "").localeCompare(a.purchased_at || a.created_at || ""));
  if (!list.length) return <div className="text-sm text-muted-foreground">None</div>;
  const issue = async (p, b) => {
    try { await api.post(`/client-packages/${p.id}/bribes/${b.id}/issue`); toast.success(`${b.name} issued${b.deduct_stock ? " — stock updated" : ""}`); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't issue"); }
  };
  const unissue = async (p, b) => {
    try { await api.post(`/client-packages/${p.id}/bribes/${b.id}/unissue`); onChanged?.(); } catch { toast.error("Couldn't undo"); }
  };
  return (
    <div className="space-y-3" data-testid="client-packages">
      {list.map((p) => {
        const plan = p.kind === "plan";
        const done = p.status === "completed" || (p.sessions_remaining || 0) <= 0;
        const expired = p.expires_at && p.expires_at < new Date().toISOString().slice(0, 10) && !done;
        return (
          <div key={p.id} className={`rounded-xl border p-3 ${done || expired ? "border-border opacity-75" : "border-primary/30 bg-primary/5"}`} data-testid={`file-package-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-1.5 flex-wrap">{plan ? <PackageCheck className="h-4 w-4 text-primary shrink-0" /> : null}<span className="truncate">{p.name}</span>
                  {!plan && <span className="text-[10px] uppercase tracking-wide rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground">Legacy</span>}
                  {done && <span className="text-[10px] uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5">Completed</span>}
                  {expired && <span className="text-[10px] uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5">Expired</span>}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {p.purchased_at || p.created_at ? `Purchased ${fmtDate(p.purchased_at || p.created_at)}` : ""}{p.price != null ? ` · Paid ${fmtCurrency(p.price, cur, 0)}` : ""}{p.expires_at ? ` · Expires ${fmtDate(p.expires_at)}` : p.expiry_from === "first_redemption" ? " · Expiry starts at first treatment" : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold tabular-nums" data-testid={`file-package-remaining-${p.id}`}>{p.sessions_remaining} / {p.sessions_total} left</div>
                <Dots total={p.sessions_total || 0} remaining={p.sessions_remaining || 0} testid={`file-package-dots-${p.id}`} />
              </div>
            </div>
            {plan && (p.allocations || []).length > 1 && (
              <div className="mt-2 text-xs space-y-0.5">{p.allocations.map((a, i) => <div key={i} className="flex justify-between"><span>{a.service_name}</span><span className="tabular-nums text-muted-foreground">{a.qty_remaining} of {a.qty_total} remaining</span></div>)}</div>
            )}
            {plan && (p.bribes || []).length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] font-mono-label uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Gift className="h-3 w-3" />Included extras</div>
                <div className="space-y-1 mt-1">
                  {p.bribes.map((b) => {
                    const issued = b.status === "issued";
                    const perk = ["price_lock", "priority_booking"].includes(b.type);
                    return (
                      <div key={b.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`file-bribe-${p.id}-${b.id}`}>
                        <span className="flex items-center gap-1.5 min-w-0">{issued || perk ? <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <Circle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}<span className="truncate">{b.qty > 1 ? `${b.qty} × ` : ""}{b.name}</span>
                          <span className="text-muted-foreground shrink-0">{perk ? "— active" : issued ? `— issued ${fmtDate(b.issued_at)}${b.issued_by_name ? ` by ${b.issued_by_name}` : ""}` : "— not yet issued"}</span></span>
                        {!perk && !issued && <Button size="sm" variant="outline" className="h-7 rounded-md text-xs px-2" onClick={() => issue(p, b)} data-testid={`issue-bribe-${b.id}`}>{b.type === "product" ? "Issue product" : "Mark given"}</Button>}
                        {!perk && issued && isAdmin && <button className="text-[11px] text-muted-foreground hover:underline" onClick={() => unissue(p, b)} data-testid={`unissue-bribe-${b.id}`}>undo</button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {plan && (
              <div className="mt-2 flex items-center gap-3 text-[11px]">
                <button className="text-primary hover:underline" onClick={() => setTermsFor(termsFor === p.id ? null : p.id)} data-testid={`file-package-terms-${p.id}`}>{termsFor === p.id ? "Hide" : "View"} original agreed terms</button>
                {(p.redemptions || []).length > 0 && <span className="text-muted-foreground">{p.redemptions.length} redemption{p.redemptions.length === 1 ? "" : "s"} · last {fmtDate(p.redemptions[p.redemptions.length - 1].at)}</span>}
              </div>
            )}
            {termsFor === p.id && (
              <ul className="mt-1.5 text-[11px] list-disc pl-4 text-muted-foreground" data-testid={`file-package-terms-list-${p.id}`}>
                {(p.terms_lines || []).map((l, i) => <li key={i}>{l}</li>)}
                <li>Agreed {fmtDate(p.terms_accepted_at)}{p.terms_channel === "online" ? " online" : " in clinic"}</li>
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};
